import { INode, IRenderContext, NodeStyleBase, SvgVisual } from '@yfiles/yfiles';
import type { NodeTip } from '../canvas/node-tooltip';

const svgns = 'http://www.w3.org/2000/svg';
let clipCount = 0;

export const LENS_NODE_WIDTH = 210;
export const LENS_NODE_HEIGHT = 66;

// status colours, fixed whatever the theme: always shown with a label too (the node's tooltip and the detail panel)
export const STATUS_COLOURS: Record<string, string> = {
    over: '#e34948', stretched: '#ec835a', healthy: '#0ca30c', under: '#2a78d6', unknown: '#a9a8a3', none: 'transparent',
    // how automated a system's work is
    automated: '#0f8b8d', mixed: '#2a78d6', assisted: '#fab219', manual: '#e34948', unassigned: '#a9a8a3',
};

// what a lens node shows
export interface LensGraphNode {
    key: string;
    kind?: 'output';                    // an output, where the work ends, drawn as a rounded tag rather than a card
    modelKey?: string;                  // the model it is in, to open it there
    label: string;
    detail: string;                     // a second line, such as "3 actions · 400 hours a year"
    segments: { colour: string; share: number }[];  // the models its work is in, as a band split by share
    status: string;                     // a key of STATUS_COLOURS
    dashed: boolean;                    // work of another kind: done by systems on the People lens, outside systems on the Systems lens
    tooltip: string;                    // in a few words, for anything that can't show the card
    tip?: NodeTip;                      // the card hovering shows: what it is and its figures
    fill?: string;                      // a heatmap colour, when one is shown
}

export interface LensGraphEdge {
    key: string;
    from: string;
    to: string;
    weight: number;                     // how much passes along it, for its thickness
    colour?: string;                    // by its kind, such as an automated or manual interface
    dashed?: boolean;
    label?: string;                     // written along it, such as the information it passes on
    tooltip: string;
}

// the node's tag: what it shows, and (while a simulation runs) its queue, which the queue badge reads
export interface LensNodeTag {
    lens: LensGraphNode;
    temporalNode?: { queue: number };
}

const text = (content: string, x: number, y: number, size: number, weight: string, fill: string, maxChars: number) => {
    const t = document.createElementNS(svgns, 'text');
    t.setAttribute('x', String(x));
    t.setAttribute('y', String(y));
    t.setAttribute('font-size', String(size));
    t.setAttribute('font-weight', weight);
    t.setAttribute('fill', fill);
    t.setAttribute('font-family', 'Inter, "Segoe UI", Arial, sans-serif');
    t.textContent = content.length > maxChars ? content.slice(0, maxChars - 1).trimEnd() + '…' : content;
    return t;
};

// a card for a lens node: a band across the top split by the models its work is in, its name and detail, and a dot for
// its status. Work of another kind (see dashed) has a dashed border
export class LensNodeStyle extends NodeStyleBase {
    protected createVisual(context: IRenderContext, node: INode): SvgVisual {
        const lens = (node.tag as LensNodeTag).lens;
        if (lens.kind === 'output') return this.outputVisual(node, lens);
        const { x, y, width, height } = node.layout;
        const g = document.createElementNS(svgns, 'g');
        g.setAttribute('transform', `translate(${x} ${y})`);

        const card = document.createElementNS(svgns, 'rect');
        card.setAttribute('width', String(width));
        card.setAttribute('height', String(height));
        card.setAttribute('rx', '8');
        card.setAttribute('fill', lens.fill || (lens.dashed ? '#f1f5f9' : '#ffffff'));
        card.setAttribute('stroke', '#94a3b8');
        card.setAttribute('stroke-width', '1');
        if (lens.dashed) card.setAttribute('stroke-dasharray', '5 3');
        g.appendChild(card);

        // the models, left to right by share, clipped to the card's rounded top
        const clipId = `lens-clip-${++clipCount}`;
        const clip = document.createElementNS(svgns, 'clipPath');
        clip.setAttribute('id', clipId);
        const clipRect = document.createElementNS(svgns, 'rect');
        clipRect.setAttribute('width', String(width));
        clipRect.setAttribute('height', String(height));
        clipRect.setAttribute('rx', '8');
        clip.appendChild(clipRect);
        g.appendChild(clip);
        const band = document.createElementNS(svgns, 'g');
        band.setAttribute('clip-path', `url(#${clipId})`);
        let left = 0;
        lens.segments.forEach((s, i) => {
            const w = i === lens.segments.length - 1 ? width - left : Math.round(width * s.share);
            const r = document.createElementNS(svgns, 'rect');
            r.setAttribute('x', String(left));
            r.setAttribute('width', String(Math.max(0, w)));
            r.setAttribute('height', '7');
            r.setAttribute('fill', s.colour);
            band.appendChild(r);
            left += w;
        });
        g.appendChild(band);

        const ink = lens.fill && isDark(lens.fill) ? '#ffffff' : '#1f2937';
        const muted = lens.fill && isDark(lens.fill) ? '#e2e8f0' : '#5b6172';
        g.appendChild(text(lens.label, 12, 30, 13, '600', ink, 26));
        g.appendChild(text(lens.detail, 12, 49, 11.5, '400', muted, 32));

        if (lens.status !== 'none') {
            const dot = document.createElementNS(svgns, 'circle');
            dot.setAttribute('cx', String(width - 13));
            dot.setAttribute('cy', '25');
            dot.setAttribute('r', '5');
            dot.setAttribute('fill', STATUS_COLOURS[lens.status] || STATUS_COLOURS['unknown']);
            dot.setAttribute('stroke', '#ffffff');
            dot.setAttribute('stroke-width', '1.5');
            g.appendChild(dot);
        }
        (g as any).__shown = signature(lens);
        return new SvgVisual(g);
    }

    // an output: a rounded tag with a dot in its model's colour
    private outputVisual(node: INode, lens: LensGraphNode): SvgVisual {
        const { x, y, width, height } = node.layout;
        const g = document.createElementNS(svgns, 'g');
        g.setAttribute('transform', `translate(${x} ${y})`);
        const tag = document.createElementNS(svgns, 'rect');
        tag.setAttribute('width', String(width));
        tag.setAttribute('height', String(height));
        tag.setAttribute('rx', String(height / 2));
        tag.setAttribute('fill', lens.fill || '#f8fafc');
        tag.setAttribute('stroke', '#64748b');
        tag.setAttribute('stroke-width', '1.5');
        g.appendChild(tag);
        const dot = document.createElementNS(svgns, 'circle');
        dot.setAttribute('cx', '22');
        dot.setAttribute('cy', String(height / 2));
        dot.setAttribute('r', '7');
        dot.setAttribute('fill', lens.segments[0]?.colour || '#a9a8a3');
        g.appendChild(dot);
        g.appendChild(text(lens.label, 38, 30, 13, '600', '#1f2937', 23));
        g.appendChild(text(lens.detail, 38, 49, 11.5, '400', '#5b6172', 28));
        (g as any).__shown = signature(lens);
        return new SvgVisual(g);
    }

    protected override updateVisual(context: IRenderContext, oldVisual: SvgVisual, node: INode): SvgVisual {
        // the card's content only changes when the graph is rebuilt, or when its colouring changes its fill or detail
        const lens = (node.tag as LensNodeTag).lens;
        const g = oldVisual.svgElement as SVGGElement;
        if ((g as any).__shown !== signature(lens)) return this.createVisual(context, node);
        g.setAttribute('transform', `translate(${node.layout.x} ${node.layout.y})`);
        return oldVisual;
    }
}

// what the card shows that can change without the graph being rebuilt
const signature = (lens: LensGraphNode) => `${lens.fill}|${lens.detail}`;

// whether text on the colour should be light
const isDark = (colour: string) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(colour);
    if (!m) return false;
    const [r, g, b] = m.slice(1).map(h => parseInt(h, 16));
    return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
};
